import {
	HttpBackend,
	HttpClient,
	HttpErrorResponse,
	HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { environement } from 'src/environments/environment';
import { ApiService } from '../services/api.service';

const PUBLIC_SUFFIXES = [
	'/auth/pre-login',
	'/auth/login',
	'/auth/email-status',
	'/auth/register',
	'/auth/register/resume',
	'/auth/register/finalize',
	'/auth/register/resend-code',
	'/auth/activate/verify',
	'/auth/activate/complete',
	'/auth/password/forgot',
	'/auth/password/reset',
	'/auth/refresh',
	'/auth/authenticate-code',
	'/auth/devices/session',
];

function isPublicAuthUrl(fullUrl: string, base: string): boolean {
	return PUBLIC_SUFFIXES.some(
		(s) => fullUrl === `${base}${s}` || fullUrl.endsWith(s),
	);
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
	const api = inject(ApiService);
	const backend = inject(HttpBackend);
	const rawClient = new HttpClient(backend);
	const router = inject(Router);
	const base = environement.API_URL.replace(/\/$/, '');

	if (!req.url.startsWith(base)) {
		return next(req);
	}

	const token = api.getAccessToken();
	let authReq = req;
	if (token) {
		authReq = req.clone({
			setHeaders: { Authorization: `Bearer ${token}` },
		});
	}

	return next(authReq).pipe(
		catchError((err: HttpErrorResponse) => {
			// Les erreurs sémantiques d'auth (compte non activé / email non
			// vérifié) doivent passer telles quelles à l'appelant pour que
			// AuthService.login bascule l'utilisateur sur le stage OTP. On
			// court-circuite le refresh sur ces codes, peu importe le status.
			const code = (err as any)?.error?.error;
			if (code === 'ACTIVATION_REQUIRED' || code === 'EMAIL_NOT_VERIFIED') {
				return throwError(() => err);
			}
			if (err.status !== 401) {
				return throwError(() => err);
			}
			if (isPublicAuthUrl(req.url, base)) {
				return throwError(() => err);
			}

			const refresh = api.getRefreshToken();
			if (!refresh) {
				api.clearTokens();
				router.navigate(['/welcome', 'sign-in']);
				return throwError(() => err);
			}

			return rawClient
				.post<{ accessToken: string; refreshToken: string }>(
					`${base}/auth/refresh`,
					{ refreshToken: refresh },
				)
				.pipe(
					switchMap((tokens) => {
						api.setTokens(tokens.accessToken, tokens.refreshToken);
						const retry = authReq.clone({
							setHeaders: {
								Authorization: `Bearer ${tokens.accessToken}`,
							},
						});
						return next(retry);
					}),
					catchError(() => {
						api.clearTokens();
						router.navigate(['/welcome', 'sign-in']);
						return throwError(() => err);
					}),
				);
		}),
	);
};
